import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { LoadAllJobPositionGroups } from 'src/app/competence/state/job-position-group.actions';
import { JobPositionGroupState } from 'src/app/competence/state/job-position-group.state';
import { UserNonConformancesRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { IdName } from 'src/app/shared/models/id-name';
import { ChangeCurrentSelectedDepartment } from 'src/app/tenant-selector/state/tenant-user-access.actions';
import { ALL_DEPS_TOKEN } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { IdNameVersion } from 'src/app/shared/models/id-name-version.model';
import { NonConformanceStatus } from '../enums/non-conformance-status.enum';
import { IListNonConformanceModel } from '../models/list-non-conformance.model';
import { INonConformanceForIO } from '../models/non-conformance-for-io.model';
import { INonConformanceStateCountResponse } from '../models/non-conformance-state-count-response.model';
import { INonConformanceReportsStatusResponse } from '../models/non-conformance-reports-status-response.model';
import { INonConformanceOpenReportsResponse } from '../models/non-conformance-open-reports-response.model';
import { INonConformanceVersion } from '../models/non-conformance-version.model';
import { INonConformanceResponse } from '../models/non-conformance.model';
import { ISubtask } from '../models/subtask.model';
import { IUserResponsibility } from '../models/user-responsibility.model';
import { NonConformanceApiService } from '../services/non-conformance-api.service';
import { NonConformanceHelperService } from '../services/non-conformance-helper.service';
import { IWindForce } from '../models/wind-force.model';
import { INotificationRecipientsResponse } from '../models/notification-recipients.model';
import {
  ClearCurrentNonConformance,
  CloseNonConformance,
  CompleteNonConformance,
  CreateNonConformance,
  CreateNonConformanceSubtask,
  DelegateNonConformance,
  DeleteNonConformance,
  DeleteNonConformanceSubtask,
  GetNonConformanceById,
  LoadNonConformanceLogs,
  LoadNonConformances,
  LoadNonConformancesForIO,
  LoadNonConformanceStateCounts,
  LoadOpenReportsForModal,
  LoadOpenReportsStatus,
  LoadReportsStatus,
  LoadNonConformanceSubtask,
  LoadNonConformanceSubtasksLogs,
  LoadNotificationRecipients,
  LoadWindDirections,
  LoadWindForces,
  PrintNonConformance,
  PrintNonConformanceReport,
  RejectNonConformance,
  ReopenNonConformance,
  RestoreNonConformance,
  SignCompletedNonConformance,
  UpdateNonConformance,
  UpdateNonConformanceSubtask,
} from './non-conformance.actions';

export interface NonConformanceStateModel {
  nonConformancesForIO: INonConformanceForIO[];
  nonConformances: IListNonConformanceModel[];
  nonConformanceTotalPages: number;
  nonConformancesState: NonConformanceStatus;
  nonConformanceLogs: INonConformanceVersion[];
  currentNonConformance: INonConformanceResponse;
  currentNonConformanceFile: Blob;
  currentSubtask?: ISubtask;
  subtasksLogs: ISubtask[];
  nonConformanceStateCounts: INonConformanceStateCountResponse | undefined;
  reportsStatus: INonConformanceReportsStatusResponse | undefined;
  openReportsStatus: INonConformanceOpenReportsResponse | undefined;
  openReportsModalData: IListNonConformanceModel[];
  nonConformanceReport: Blob | undefined;
  userResponsibility: IUserResponsibility;
  windForces: IWindForce[];
  windDirections: IdName[];
  notificationRecipients: INotificationRecipientsResponse | undefined;
}

const NON_CONFORMANCE_STATE_TOKEN = new StateToken<NonConformanceStateModel>('nonConformances');

@State<NonConformanceStateModel>({
  name: NON_CONFORMANCE_STATE_TOKEN,
  defaults: {
    nonConformancesForIO: [],
    nonConformances: [],
    nonConformanceTotalPages: 0,
    nonConformancesState: NonConformanceStatus.Open,
    nonConformanceLogs: [],
    currentNonConformance: <INonConformanceResponse>{},
    currentNonConformanceFile: <Blob>{},
    currentSubtask: undefined,
    subtasksLogs: [],
    nonConformanceStateCounts: undefined,
    reportsStatus: undefined,
    openReportsStatus: undefined,
    openReportsModalData: [],
    nonConformanceReport: undefined,
    userResponsibility: {
      isResponsibleForFollowUp: false,
      isResponsibleForClosing: false,
      isResponsible: false,
    },
    windForces: [],
    windDirections: [],
    notificationRecipients: undefined,
  },
})
@Injectable()
export class NonConformanceState {
  constructor(
    private service: NonConformanceApiService,
    private store: Store,
    private helper: NonConformanceHelperService
  ) {}

  @Action(LoadNonConformances, { cancelUncompleted: true })
  loadNonConformances(
    { getState, setState }: StateContext<NonConformanceStateModel>,
    { state, filter, allDeps, relatedNonConformancesOnly }: LoadNonConformances
  ) {
    const ncStateChecker = state || getState().nonConformancesState;
    return this.service
      .getAllNonConformancesByState(
        ncStateChecker,
        filter,
        allDeps || getDepartmentId() === ALL_DEPS_TOKEN,
        relatedNonConformancesOnly
      )
      .pipe(
        tap((response: ApiResponse<IListNonConformanceModel[]>) => {
          setState({
            ...getState(),
            nonConformances: response.data,
            nonConformanceTotalPages: response.totalPages,
            nonConformancesState: ncStateChecker,
          });
        })
      );
  }

  @Action(LoadNonConformancesForIO, { cancelUncompleted: true })
  loadNonConformancesForIO({ getState, setState }: StateContext<NonConformanceStateModel>) {
    return this.service.getNonConformancesForIO().pipe(
      tap((response: INonConformanceForIO[]) => {
        setState({
          ...getState(),
          nonConformancesForIO: response,
        });
      })
    );
  }

  @Action(CreateNonConformance, { cancelUncompleted: true })
  @OnSuccess({ url: UserNonConformancesRoutes.Open, message: 'nonConformanceCreated' })
  createNonConformance(
    { getState, setState }: StateContext<NonConformanceStateModel>,
    { nonConformance }: CreateNonConformance
  ) {
    return this.service.createNonConformance(nonConformance);
  }

  @Action(UpdateNonConformance, { cancelUncompleted: true })
  @OnSuccess({ message: 'nonConformanceUpdated' })
  updateNonConformance(
    { getState, setState }: StateContext<NonConformanceStateModel>,
    { nonConformance }: UpdateNonConformance
  ) {
    return this.service.updateNonConformance(nonConformance).pipe(
      tap((response: INonConformanceResponse) => {
        response.type = <IdNameVersion>{ id: nonConformance.typeId };
        const link = UserNonConformancesRoutes.ShowOverview.replace(':id', nonConformance.id);
        redirectAndBypassUnsavedDataCheck(link);
      })
    );
  }

  @Action(GetNonConformanceById, { cancelUncompleted: true })
  getNonConformanceById(
    { getState, setState }: StateContext<NonConformanceStateModel>,
    { id }: GetNonConformanceById
  ) {
    const positionGroups = this.store.selectSnapshot(JobPositionGroupState.getAllJobPositionGroups);
    return of(positionGroups || this.store.dispatch(new LoadAllJobPositionGroups())).pipe(
      switchMap(() => {
        const allDeps = getDepartmentId() === ALL_DEPS_TOKEN;
        if (allDeps) {
          const ncDetails = getState().nonConformances.find(
            (nc: IListNonConformanceModel) => nc.id === id
          );
          if (ncDetails?.department?.id) {
            const ncDepartmentId = ncDetails.department.id;
            this.store.dispatch([new ChangeCurrentSelectedDepartment(ncDepartmentId, false, true)]);
            return this.service.getNonConformanceById(id, ncDepartmentId).pipe(
              tap((nonConformance: INonConformanceResponse) => {
                const state = getState();
                setState({
                  ...state,
                  currentNonConformance: nonConformance,
                });
              }),
              switchMap((nonConformance: INonConformanceResponse) =>
                this.helper.checkIfCurrentUserResponsible(nonConformance)
              ),
              tap((userResponsibility: IUserResponsibility) => {
                setState({
                  ...getState(),
                  userResponsibility,
                });
              })
            );
          }
        }

        return this.service.getNonConformanceById(id).pipe(
          tap((nonConformance: INonConformanceResponse) => {
            setState({
              ...getState(),
              currentNonConformance: nonConformance,
            });
          }),
          switchMap((nonConformance: INonConformanceResponse) =>
            this.helper.checkIfCurrentUserResponsible(nonConformance)
          ),
          tap((userResponsibility: IUserResponsibility) => {
            setState({
              ...getState(),
              userResponsibility,
            });
          })
        );
      })
    );
  }

  @Action(DeleteNonConformance, { cancelUncompleted: true })
  @OnSuccess({
    url: UserNonConformancesRoutes.Open,
    message: 'nonConformanceDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteNonConformance(ctx: StateContext<NonConformanceStateModel>, { id }: DeleteNonConformance) {
    return this.service.deleteNonConformance(id);
  }

  @Action(DelegateNonConformance, { cancelUncompleted: true })
  @OnSuccess({ message: 'nonConformanceDelegated' })
  delegateNonConformance(
    ctx: StateContext<NonConformanceStateModel>,
    { delegateNonConformance }: DelegateNonConformance
  ) {
    return this.service.delegateNonConformance(delegateNonConformance).pipe(
      tap(() => {
        ctx.dispatch(new LoadNonConformanceLogs(delegateNonConformance.id));
      })
    );
  }

  @Action(CloseNonConformance, { cancelUncompleted: true })
  @OnSuccess({ message: 'nonConformanceClosed' })
  closeNonConformance(
    ctx: StateContext<NonConformanceStateModel>,
    { idComment }: CloseNonConformance
  ) {
    return this.service.closeNonConformance(idComment).pipe(
      tap(() => {
        const state = ctx.getState();
        const nonConformance = { ...state.currentNonConformance };
        nonConformance.nonConformanceState = NonConformanceStatus.Closed;
        ctx.setState({
          ...state,
          currentNonConformance: nonConformance,
        });
        ctx.dispatch(new LoadNonConformanceLogs(idComment.id));
      })
    );
  }

  @Action(ReopenNonConformance, { cancelUncompleted: true })
  @OnSuccess({ message: 'nonConformanceReopened' })
  reopenNonConformance(
    ctx: StateContext<NonConformanceStateModel>,
    { idComment }: ReopenNonConformance
  ) {
    return this.service.reopenNonConformance(idComment).pipe(
      tap(() => {
        const state = ctx.getState();
        const nonConformance = { ...state.currentNonConformance };
        nonConformance.nonConformanceState = NonConformanceStatus.Open;
        ctx.setState({
          ...state,
          currentNonConformance: nonConformance,
        });
        ctx.dispatch(new LoadNonConformanceLogs(idComment.id));
      })
    );
  }

  @Action(RestoreNonConformance, { cancelUncompleted: true })
  @OnSuccess({ message: 'nonConformanceRestored', url: UserNonConformancesRoutes.Open })
  restoreNonConformance(
    ctx: StateContext<NonConformanceStateModel>,
    { id }: RestoreNonConformance
  ) {
    const allDeps = getDepartmentId() === ALL_DEPS_TOKEN;
    if (allDeps) {
      const ncDetails = ctx
        .getState()
        .nonConformances.find((nc: IListNonConformanceModel) => nc.id === id);
      if (ncDetails?.department?.id) {
        const ncDepartmentId = ncDetails.department.id;
        this.store.dispatch([new ChangeCurrentSelectedDepartment(ncDepartmentId, false, true)]);
        return this.service.restoreNonConformance(id, ncDepartmentId);
      }
    }

    return this.service.restoreNonConformance(id);
  }

  @Action(CompleteNonConformance, { cancelUncompleted: true })
  @OnSuccess({ message: 'nonConformanceCompleted' })
  completeNonConformance(
    ctx: StateContext<NonConformanceStateModel>,
    { idComment }: CompleteNonConformance
  ) {
    return this.service.completeNonConformance(idComment).pipe(
      tap(() => {
        const state = ctx.getState();
        const nonConformance = { ...state.currentNonConformance };
        nonConformance.nonConformanceState = NonConformanceStatus.Completed;
        ctx.setState({
          ...state,
          currentNonConformance: nonConformance,
        });
        ctx.dispatch(new LoadNonConformanceLogs(idComment.id));
      })
    );
  }

  @Action(RejectNonConformance, { cancelUncompleted: true })
  @OnSuccess({ message: 'nonConformanceRejected' })
  rejectNonConformance(
    ctx: StateContext<NonConformanceStateModel>,
    { idComment }: RejectNonConformance
  ) {
    return this.service.rejectNonConformance(idComment).pipe(
      tap(() => {
        const state = ctx.getState();
        const nonConformance = { ...state.currentNonConformance };
        nonConformance.nonConformanceState = NonConformanceStatus.Completed;
        ctx.setState({
          ...state,
          currentNonConformance: nonConformance,
        });
        ctx.dispatch(new LoadNonConformanceLogs(idComment.id));
      })
    );
  }

  @Action(LoadNonConformanceLogs, { cancelUncompleted: true })
  loadNonConformanceLogs(
    { getState, setState }: StateContext<NonConformanceStateModel>,
    { id }: LoadNonConformanceLogs
  ) {
    return this.service.getNonConformanceLogs(id).pipe(
      tap((nonConformanceLogs: INonConformanceVersion[]) => {
        const state = getState();
        setState({
          ...state,
          nonConformanceLogs,
        });
      })
    );
  }

  @Action(PrintNonConformance, { cancelUncompleted: true })
  printNonConformance(
    { getState, setState }: StateContext<NonConformanceStateModel>,
    { printNonConformance }: PrintNonConformance
  ) {
    return this.service.downloadNonConformanceFile(printNonConformance).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          currentNonConformanceFile: file,
        });
      })
    );
  }

  @Action(SignCompletedNonConformance, { cancelUncompleted: true })
  @OnSuccess({ message: 'nonConformanceUpdated' })
  signCompletedNonConformance(
    ctx: StateContext<NonConformanceStateModel>,
    { signNonConformance }: SignCompletedNonConformance
  ) {
    return this.service.signCompletedNonConformance(signNonConformance).pipe(
      tap(() => {
        const link = UserNonConformancesRoutes.ShowOverview.replace(':id', signNonConformance.id);
        redirectAndBypassUnsavedDataCheck(link);
      })
    );
  }

  @Action(ClearCurrentNonConformance, { cancelUncompleted: true })
  clearCurrentNonConformanceType({ getState, setState }: StateContext<NonConformanceStateModel>) {
    const state = getState();
    setState({
      ...state,
      currentNonConformance: <INonConformanceResponse>{},
    });
  }

  @Action(LoadNonConformanceStateCounts, { cancelUncompleted: true })
  getNonConformanceStateCounts({ getState, setState }: StateContext<NonConformanceStateModel>) {
    return this.service.getNonConformanceStateCounts().pipe(
      tap((nonConformanceStateCounts: INonConformanceStateCountResponse) => {
        const state = getState();
        setState({
          ...state,
          nonConformanceStateCounts,
        });
      })
    );
  }

  @Action(LoadReportsStatus, { cancelUncompleted: true })
  loadReportsStatus(
    { getState, setState }: StateContext<NonConformanceStateModel>,
    { departmentIds, from, to }: LoadReportsStatus
  ) {
    return this.service.getReportsStatus(departmentIds, from, to).pipe(
      tap((reportsStatus: INonConformanceReportsStatusResponse) => {
        const state = getState();
        setState({
          ...state,
          reportsStatus,
        });
      })
    );
  }

  @Action(LoadOpenReportsStatus, { cancelUncompleted: true })
  loadOpenReportsStatus(
    { getState, setState }: StateContext<NonConformanceStateModel>,
    { severityIds, departmentIds }: LoadOpenReportsStatus
  ) {
    return this.service.getOpenReportsStatus(severityIds, departmentIds).pipe(
      tap((openReportsStatus: INonConformanceOpenReportsResponse) => {
        const state = getState();
        setState({
          ...state,
          openReportsStatus,
        });
      })
    );
  }

  @Action(LoadOpenReportsForModal, { cancelUncompleted: true })
  loadOpenReportsForModal(
    { getState, setState }: StateContext<NonConformanceStateModel>,
    { filter, relatedNonConformancesOnly }: LoadOpenReportsForModal
  ) {
    return this.service
      .getAllNonConformancesByState(
        filter.additional || NonConformanceStatus.Open,
        filter,
        true,
        relatedNonConformancesOnly
      )
      .pipe(
        tap((response: ApiResponse<IListNonConformanceModel[]>) => {
          setState({
            ...getState(),
            openReportsModalData: response.data,
          });
        })
      );
  }

  @Action(PrintNonConformanceReport, { cancelUncompleted: true })
  printNonConformanceReport(
    { getState, setState }: StateContext<NonConformanceStateModel>,
    { filter, menuId, status }: PrintNonConformanceReport
  ) {
    return this.service.printReport(filter, menuId, status).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          nonConformanceReport: file,
        });
      })
    );
  }

  @Action(LoadNonConformanceSubtask, { cancelUncompleted: true })
  loadNonConformanceSubtask(
    { getState, setState }: StateContext<NonConformanceStateModel>,
    { ncId, subtaskId }: LoadNonConformanceSubtask
  ) {
    return this.service.getSubtask(ncId, subtaskId).pipe(
      tap((subtask: ISubtask) => {
        const state = getState();
        setState({
          ...state,
          currentSubtask: subtask,
        });
      })
    );
  }

  @Action(CreateNonConformanceSubtask)
  @OnSuccess({
    message: 'subtaskCreated',
  })
  createNonConformanceSubtask(
    ctx: StateContext<NonConformanceStateModel>,
    { ncId, subtask }: CreateNonConformanceSubtask
  ) {
    return this.service.createSubtask(ncId, subtask).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(
          UserNonConformancesRoutes.ShowOverview.replace(':id', ncId)
        );
      })
    );
  }

  @Action(UpdateNonConformanceSubtask)
  @OnSuccess({
    message: 'subtaskUpdated',
  })
  updateNonConformanceSubtask(
    ctx: StateContext<NonConformanceStateModel>,
    { ncId, subtaskId, subtask }: UpdateNonConformanceSubtask
  ) {
    return this.service.updateSubtask(ncId, subtaskId, subtask).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(
          UserNonConformancesRoutes.ShowOverview.replace(':id', ncId)
        );
      })
    );
  }

  @Action(DeleteNonConformanceSubtask)
  @OnSuccess({
    message: 'subtaskDeleted',
  })
  deleteNonConformanceSubtask(
    ctx: StateContext<NonConformanceStateModel>,
    { ncId, subtaskId }: DeleteNonConformanceSubtask
  ) {
    return this.service.deleteSubtask(ncId, subtaskId).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(
          UserNonConformancesRoutes.ShowOverview.replace(':id', ncId)
        );
      })
    );
  }

  @Action(LoadNonConformanceSubtasksLogs, { cancelUncompleted: true })
  loadNonConformanceSubtasksLogs(
    { getState, setState }: StateContext<NonConformanceStateModel>,
    { ncId }: LoadNonConformanceSubtasksLogs
  ) {
    return this.service.getSubtasksLogs(ncId).pipe(
      tap((subtasksLogs: ISubtask[]) => {
        const state = getState();
        setState({
          ...state,
          subtasksLogs: (subtasksLogs || []).sort(
            (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
          ),
        });
      })
    );
  }

  @Action(LoadWindForces, { cancelUncompleted: true })
  loadWindForces(ctx: StateContext<NonConformanceStateModel>) {
    return this.service.getWindForces().pipe(
      tap((windForces: IWindForce[]) => {
        ctx.patchState({
          windForces: windForces || [],
        });
      })
    );
  }

  @Action(LoadWindDirections, { cancelUncompleted: true })
  loadWindDirections(ctx: StateContext<NonConformanceStateModel>) {
    return this.service.getWindDirections().pipe(
      tap((windDirections: IdName[]) => {
        ctx.patchState({
          windDirections: windDirections || [],
        });
      })
    );
  }

  @Action(LoadNotificationRecipients, { cancelUncompleted: true })
  loadNotificationRecipients(
    ctx: StateContext<NonConformanceStateModel>,
    { id, actionType }: LoadNotificationRecipients
  ) {
    return this.service.getNotificationRecipients(id, actionType).pipe(
      tap((notificationRecipients: INotificationRecipientsResponse) => {
        ctx.patchState({
          notificationRecipients,
        });
      })
    );
  }

  @Selector()
  static getNonConformanceTotalPages(state: NonConformanceStateModel) {
    return state.nonConformanceTotalPages;
  }

  @Selector()
  static getNonConformancesForIO(state: NonConformanceStateModel) {
    return state.nonConformancesForIO;
  }

  @Selector()
  static getNonConformances(state: NonConformanceStateModel) {
    return state.nonConformances;
  }

  @Selector()
  static getNonConformanceById(state: NonConformanceStateModel) {
    return state.currentNonConformance;
  }

  @Selector()
  static getNonConformanceLogs(state: NonConformanceStateModel) {
    return state.nonConformanceLogs;
  }

  @Selector()
  static getNonConformanceFile(state: NonConformanceStateModel) {
    return state.currentNonConformanceFile;
  }

  @Selector()
  static getNonConformanceStateCounts(state: NonConformanceStateModel) {
    return state.nonConformanceStateCounts;
  }

  @Selector()
  static getReportsStatus(state: NonConformanceStateModel) {
    return state.reportsStatus;
  }

  @Selector()
  static getOpenReportsStatus(state: NonConformanceStateModel) {
    return state.openReportsStatus;
  }

  @Selector()
  static getOpenReportsModalData(state: NonConformanceStateModel) {
    return state.openReportsModalData;
  }

  @Selector()
  static getNonConformanceReport(state: NonConformanceStateModel) {
    return state.nonConformanceReport;
  }

  @Selector()
  static getUserResponsibility(state: NonConformanceStateModel) {
    return state.userResponsibility;
  }

  @Selector()
  static getIsResponsibleForFollowUp(state: NonConformanceStateModel) {
    return state.userResponsibility.isResponsibleForFollowUp;
  }

  @Selector()
  static getIsResponsibleForClosing(state: NonConformanceStateModel) {
    return state.userResponsibility.isResponsibleForClosing;
  }

  @Selector()
  static getIsResponsible(state: NonConformanceStateModel) {
    return state.userResponsibility.isResponsible;
  }

  @Selector()
  static getCurrentSubtask(state: NonConformanceStateModel) {
    return state.currentSubtask;
  }

  @Selector()
  static getSubtasksLogs(state: NonConformanceStateModel) {
    return state.subtasksLogs;
  }

  @Selector()
  static getWindForces(state: NonConformanceStateModel): IWindForce[] {
    return state.windForces;
  }

  @Selector()
  static getWindDirections(state: NonConformanceStateModel): IdName[] {
    return state.windDirections;
  }

  @Selector()
  static getNonConformancesState(state: NonConformanceStateModel): NonConformanceStatus {
    return state.nonConformancesState;
  }

  @Selector()
  static getNotificationRecipients(
    state: NonConformanceStateModel
  ): INotificationRecipientsResponse | undefined {
    return state.notificationRecipients;
  }
}
